import React from 'react';
import { Box, Flex, Text, VStack, Spinner } from '@chakra-ui/react';
import { conversationTitle } from './phone';
import { dayLabel } from './time';
import {
	useGetConversationQuery,
	useListMessagesQuery,
	useMarkReadMutation,
} from 'api/metaWhatsappApi';
import { useMetaWhatsappConversation } from '../hooks/useMetaWhatsappSocket';
import useOlderMessages from '../hooks/useOlderMessages';
import { usePrefs } from '../hooks/useLocalPrefs';
import { useAgentNames, useAgentAvatars } from '../hooks/useAgentName';
import { useTyping } from '../hooks/useTyping';
import ChatHeader from './ChatHeader';
import Composer from './Composer';
import OwnershipBar from './OwnershipBar';
import MessageBubble from './MessageBubble';
import NewConversationBanner from './NewConversationBanner';
import Wallpaper from './Wallpaper';
import UnreadDivider from './UnreadDivider';
import SystemNote from './SystemNote';
import JumpToLatest from './JumpToLatest';
import HistoryLoader from './HistoryLoader';
import TypingIndicator from './TypingIndicator';
import MediaLightbox from './MediaLightbox';

/**
 * One thread.
 *
 * Messages arrive newest-first from the server (that is the indexed order), and
 * are reversed for display — the query stays cursor-friendly while the reader
 * sees a conversation. Scrolling up loads older pages behind the newest one
 * without moving what is on screen. Consecutive messages from one side are a
 * run (tail on the first, tight stack after), a date pill separates days, and
 * an "unread" line marks where the reader left off.
 *
 * ★ A NEW MESSAGE NEVER YANKS THE VIEW. If the reader is at the bottom (or
 * just sent something) the thread follows; otherwise a button counts what
 * arrived and one click catches up.
 *
 * A `403` here is not a bug: access can be revoked while a thread is open, and
 * the socket will have said so. The view degrades to an explanation rather than
 * an empty box.
 */

const NEAR_BOTTOM_PX = 120;
const keyOf = (m) => m.wamid ?? m._id;

/**
 * Turn a flat, oldest-first list into what the screen draws: a day pill before
 * the first message of each day, the unread line before the first of the last
 * `unread` inbound messages, and `first` on the first bubble of each run.
 */
const layout = (messages, unread) => {
	const out = [];
	let lastDay = null;
	let lastSide = null;

	let unreadAt = -1;
	if (unread > 0) {
		let seen = 0;
		for (let i = messages.length - 1; i >= 0; i -= 1) {
			if (messages[i].direction === 'inbound') {
				seen += 1;
				if (seen === unread) {
					unreadAt = i;
					break;
				}
			}
		}
	}

	messages.forEach((m, i) => {
		const day = new Date(m.occurredAt).toDateString();
		if (day !== lastDay) {
			out.push({ kind: 'day', key: `day-${day}`, label: dayLabel(m.occurredAt) });
			lastDay = day;
			lastSide = null;
		}
		if (i === unreadAt) {
			out.push({ kind: 'unread', key: 'unread', count: unread });
			lastSide = null;
		}
		// Something the CRM recorded, that nobody sent — a line across the
		// thread, not a bubble. `lastSide` is cleared so the next real message
		// starts a fresh run and gets its tail back.
		if (m.type === 'system') {
			out.push({ kind: 'system', key: keyOf(m), text: m.content });
			lastSide = null;
			return;
		}
		const first = m.direction !== lastSide;
		out.push({ kind: 'message', key: keyOf(m), message: m, first });
		lastSide = m.direction;
	});
	return out;
};

function DayPill({ label }) {
	return (
		<Flex justify="center" my="3">
			<Text
				fontSize="0.7rem"
				fontWeight="medium"
				color="wa.inkMuted"
				bg="wa.datePill"
				px="3"
				py="1.5"
				borderRadius="md"
				boxShadow="0 1px 0.5px rgba(0,0,0,0.13)"
				textTransform="uppercase"
				letterSpacing="wide"
			>
				{label}
			</Text>
		</Flex>
	);
}

/**
 * @param {object} [numberState]  only the lead drawer supplies this. A thread
 *   has no opinion about whose number it is on — that is a question about a
 *   LEAD, and this component is opened from the inbox too, where there is no
 *   lead in view and the same thread is perfectly current. So the fact arrives
 *   as a prop rather than being read from the conversation.
 *   `{ isPreviousNumber: boolean, changedAt: string|null }`
 */
export default function ConversationView({ conversationId, onBack, onOpenInfo, numberState = null }) {
	const { data: conversation, isLoading, error } = useGetConversationQuery(conversationId, {
		skip: !conversationId,
	});
	const { data: headData, isLoading: loadingMessages } = useListMessagesQuery(
		{ conversationId },
		{ skip: !conversationId },
	);
	const { older, loadOlder, isFetching: loadingOlder, hasMore } = useOlderMessages({
		conversationId,
		headCursor: headData?.nextCursor,
	});
	const [markRead] = useMarkReadMutation();
	const p = usePrefs();
	// ★ WHO WROTE WHAT, FROM THE THREAD ITSELF.
	// The server answers `participants` alongside the messages, so a name and a
	// profile picture exist for every sender on the page without the inbox
	// depending on a Redux slice that only the users admin screen ever fills.
	const participants = headData?.participants ?? null;
	const names = useAgentNames(conversation, participants);
	const avatars = useAgentAvatars(participants);
	const typing = useTyping(conversationId);

	const scrollRef = React.useRef(null);
	const bottomRef = React.useRef(null);
	const topRef = React.useRef(null);
	const atBottomRef = React.useRef(true);
	const anchorRef = React.useRef(null);
	const [atBottom, setAtBottom] = React.useState(true);
	// ★ COUNTED FROM THE MESSAGES THEMSELVES, NOT FROM RENDERS.
	// This used to be `setPendingNew(n => n + 1)` inside an effect keyed on the
	// newest message's key — and that key CHANGES for a message already counted,
	// because an outbound bubble starts life keyed on its client id and is
	// re-keyed on the server's wamid the moment the send is acknowledged. One
	// arrival could therefore be counted twice. Holding the keys makes the badge
	// a fact about the conversation rather than about React.
	const [pendingKeys, setPendingKeys] = React.useState(() => new Set());
	const pendingNew = pendingKeys.size;
	const [replyTo, setReplyTo] = React.useState(null);
	const [highlightWamid, setHighlightWamid] = React.useState(null);
	const [lightbox, setLightbox] = React.useState(null); // index into `gallery`

	// Join this thread's room while it is on screen: ticks, typing and ownership
	// changes are delivered there. The server re-resolves on every open, so this
	// widens nothing.
	useMetaWhatsappConversation(conversationId);

	// ★ THE UNREAD COUNT IS CAPTURED BEFORE IT IS CLEARED. The mark-read below
	// makes the next refetch say 0; the divider needs the number from the moment
	// the thread was opened, once per thread.
	const unreadRef = React.useRef({ id: null, count: 0 });
	if (conversation && unreadRef.current.id !== conversationId) {
		unreadRef.current = { id: conversationId, count: conversation.unreadCount ?? 0 };
	}
	const unreadCount = unreadRef.current.id === conversationId ? unreadRef.current.count : 0;

	// Opening a thread clears its badge. Fire-and-forget: a failed mark-read is
	// not worth interrupting the agent for.
	React.useEffect(() => {
		if (conversationId && conversation?.unreadCount > 0) {
			markRead(conversationId).catch(() => {});
		}
	}, [conversationId, conversation?.unreadCount, markRead]);

	// Newest page (live) + older pages (state), deduped, oldest-first.
	const messages = React.useMemo(() => {
		const seen = new Set();
		const out = [];
		for (const m of [...(headData?.messages ?? []), ...older]) {
			const k = keyOf(m);
			if (seen.has(k)) continue;
			seen.add(k);
			out.push(m);
		}
		return out.reverse();
	}, [headData, older]);

	const byWamid = React.useMemo(() => {
		const map = new Map();
		messages.forEach((m) => m.wamid && map.set(m.wamid, m));
		return map;
	}, [messages]);

	// ★ EVERY OUTBOUND RUN IS ATTRIBUTED, NOT ONLY THE BUSY THREADS.
	// This used to require two distinct senders before any label appeared, so
	// the commonest case — one agent, and a supervisor reading over their
	// shoulder — showed no attribution at all. The reviewer's whole question is
	// "who said this", and a thread does not stop having an author because it
	// only has one. Runs still collapse: the avatar and name are drawn once per
	// run of consecutive messages from the same person, the way a group chat
	// does it, so nothing becomes noisier.

	const gallery = React.useMemo(
		() => messages.filter((m) => (m.type === 'image' || m.type === 'video') && m.wamid),
		[messages],
	);

	const items = React.useMemo(() => layout(messages, unreadCount), [messages, unreadCount]);

	// Which bubbles are new since the thread was drawn — those animate in. Both
	// the client id and the wamid are remembered, so the optimistic bubble does
	// not animate a second time when its key flips on the server's answer.
	const knownRef = React.useRef({ id: null, keys: new Set(), primed: false });
	if (knownRef.current.id !== conversationId) {
		knownRef.current = { id: conversationId, keys: new Set(), primed: false };
	}
	const known = knownRef.current;
	React.useEffect(() => {
		messages.forEach((m) => {
			if (m.wamid) known.keys.add(m.wamid);
			if (m._id) known.keys.add(m._id);
		});
		if (!loadingMessages) known.primed = true;
	});

	// ── Scrolling ────────────────────────────────────────────────────────
	// ★ THE PANE SCROLLS, NEVER THE WINDOW. `scrollIntoView` walks up every
	// scrollable ancestor, so on a short screen — where the shell is a little
	// taller than the space left — it scrolled the PAGE too, taking the chat
	// header and the thread list off the top with it. Setting `scrollTop` on
	// the container cannot touch anything outside it.
	const scrollToBottom = React.useCallback((behavior = 'smooth') => {
		const el = scrollRef.current;
		if (el) el.scrollTo({ top: el.scrollHeight, behavior });
		setPendingKeys((prev) => (prev.size ? new Set() : prev));
	}, []);

	// ★ A PHOTO THAT LOADS AFTER THE SCROLL UNDOES THE SCROLL.
	// Opening a thread lands at the bottom immediately, but images, videos and
	// voice notes have no height until their bytes arrive — so the content grows
	// underneath a reader who is supposedly "at the latest message" and quietly
	// leaves them above it. Re-pinning on any growth WHILE AT THE BOTTOM is what
	// makes "opens at the newest message" true for a thread with media in it,
	// and it deliberately does nothing at all once the reader has scrolled up.
	React.useEffect(() => {
		const el = scrollRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return undefined;
		const observer = new ResizeObserver(() => {
			// `atBottomRef` and not `atBottom`: the ref is current within the same
			// frame, and this fires during layout.
			if (atBottomRef.current) el.scrollTop = el.scrollHeight;
		});
		// The inner column, whose height IS the content height.
		if (el.firstElementChild) observer.observe(el.firstElementChild);
		return () => observer.disconnect();
	}, [conversationId]);

	const newestKey = items.length ? items[items.length - 1].key : null;
	const newestOutbound = messages.length ? messages[messages.length - 1].direction === 'outbound' : false;
	const lastConversation = React.useRef(null);
	React.useEffect(() => {
		if (!newestKey) return;
		if (lastConversation.current !== conversationId) {
			// A different thread: land at the bottom without a visible scroll.
			lastConversation.current = conversationId;
			scrollToBottom('auto');
			return;
		}
		if (atBottomRef.current || newestOutbound) scrollToBottom('smooth');
		else {
			setPendingKeys((prev) => {
				if (prev.has(newestKey)) return prev;
				const next = new Set(prev);
				next.add(newestKey);
				return next;
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newestKey, conversationId]);

	// Older pages are prepended; keep what the reader was looking at in place.
	//
	// ★ RESTORING THE DISTANCE FROM THE BOTTOM, NOT THE DISTANCE FROM THE TOP.
	// Everything new arrives ABOVE the reader, so how far they sit from the end
	// of the thread is the quantity that did not change. `scrollHeight - height
	// + top` is exactly that, and it stays exact however many messages land and
	// whatever they contain.
	//
	// ⚠ KEYED ON `older`, NOT ON `older.length`. A page that comes back EMPTY —
	// the last page of a thread often does — leaves the length untouched, so an
	// effect keyed on length would never run, the anchor would never be cleared,
	// and the guard in `requestOlder` would refuse every later page: pagination
	// would silently lock up one page from the end. The array identity changes
	// whenever a page lands, empty or not.
	React.useLayoutEffect(() => {
		const el = scrollRef.current;
		const anchor = anchorRef.current;
		if (!el || !anchor) return;
		el.scrollTop = el.scrollHeight - anchor.height + anchor.top;
		anchorRef.current = null;
	}, [older]);

	const requestOlder = React.useCallback(() => {
		const el = scrollRef.current;
		if (!el || !hasMore || loadingOlder) return;
		// ⚠ NEVER OVERWRITE AN ANCHOR THAT HAS NOT BEEN APPLIED YET. The observer
		// can fire again before the page lands — `loadOlder` is guarded against a
		// duplicate FETCH, but a second measurement taken mid-flight would record
		// a position the reader is no longer at, and the applied correction would
		// put them somewhere they never were.
		if (anchorRef.current) return;
		// The distance from the BOTTOM is what is invariant when history is added
		// to the top. Restoring it is exact however much arrives, and does not
		// depend on any element still existing after the re-render.
		anchorRef.current = { height: el.scrollHeight, top: el.scrollTop };
		// If no page actually ran, nothing will consume this anchor — drop it now
		// rather than leave it blocking every later request.
		loadOlder().then((landed) => {
			if (!landed) anchorRef.current = null;
		});
	}, [hasMore, loadingOlder, loadOlder]);

	// The top sentinel: scrolling into it asks for the page before.
	React.useEffect(() => {
		const el = topRef.current;
		const root = scrollRef.current;
		if (!el || !root || !hasMore || typeof IntersectionObserver === 'undefined') return undefined;
		const io = new IntersectionObserver((entries) => entries[0]?.isIntersecting && requestOlder(), {
			root,
			rootMargin: '200px 0px 0px 0px',
		});
		io.observe(el);
		return () => io.disconnect();
	}, [hasMore, requestOlder, conversationId]);

	const onScroll = () => {
		const el = scrollRef.current;
		if (!el) return;
		const near = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
		atBottomRef.current = near;
		setAtBottom((prev) => (prev === near ? prev : near));
		if (near) setPendingKeys((prev) => (prev.size ? new Set() : prev));
	};

	// ── Replies ──────────────────────────────────────────────────────────
	React.useEffect(() => {
		setReplyTo(null);
		// A scroll anchor from the previous thread must not be applied to this one.
		anchorRef.current = null;
		setPendingKeys((prev) => (prev.size ? new Set() : prev));
	}, [conversationId]);

	const jumpTo = React.useCallback((wamid) => {
		const el = document.getElementById(`wa-msg-${wamid}`);
		const box = scrollRef.current;
		if (!el || !box) return;
		// Centred within the pane, computed rather than delegated — see
		// scrollToBottom for why scrollIntoView is not used here.
		const offset = el.getBoundingClientRect().top - box.getBoundingClientRect().top;
		const top = box.scrollTop + offset - (box.clientHeight - el.clientHeight) / 2;
		box.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
		setHighlightWamid(wamid);
		setTimeout(() => setHighlightWamid((h) => (h === wamid ? null : h)), 1200);
	}, []);

	const openMedia = React.useCallback(
		(wamid) => {
			const i = gallery.findIndex((m) => m.wamid === wamid);
			if (i >= 0) setLightbox(i);
		},
		[gallery],
	);

	const customerName = conversationTitle(conversation);
	const quoteSenderOf = (q) => (!q ? '' : q.direction === 'outbound' ? names(q.sentBy) : customerName);

	// ── Empty / error states ─────────────────────────────────────────────
	if (!conversationId) {
		return (
			<Flex h="100%" align="center" justify="center" bg="wa.panel" direction="column" position="relative">
				<Wallpaper />
				<Text fontSize="lg" fontWeight="medium" color="wa.ink" position="relative">
					WhatsApp inbox
				</Text>
				<Text fontSize="sm" color="wa.inkMuted" mt="1" position="relative">
					Pick a chat on the left to read and reply.
				</Text>
			</Flex>
		);
	}

	if (error) {
		const revoked = error.status === 403;
		return (
			<Flex h="100%" align="center" justify="center" bg="wa.panel" px="6">
				<VStack spacing="1">
					<Text fontSize="sm" color="wa.ink">
						{revoked ? 'You no longer have access to this conversation.' : 'This conversation could not be loaded.'}
					</Text>
					{revoked && (
						<Text fontSize="xs" color="wa.inkMuted">
							Your assignment or lead access may have changed.
						</Text>
					)}
				</VStack>
			</Flex>
		);
	}

	if (isLoading || !conversation) {
		return (
			<Flex h="100%" align="center" justify="center" bg="wa.panel">
				<Spinner size="sm" color="wa.inkMuted" />
			</Flex>
		);
	}

	return (
		<Flex direction="column" h="100%" bg="wa.panel" minH="0" overflow="hidden">
			{/* ★ THE THREE FIXED REGIONS DECLARE THAT THEY DO NOT SHRINK.
			    A flex child defaults to `flex-shrink: 1`, so on a short window the
			    header, the ownership bar and the composer were all candidates for
			    compression before the message area gave up a single pixel — which
			    is why the composer looked squashed and the header drifted on
			    laptop screens. Only the message list is elastic. */}
			<Box flexShrink={0}>
				<ChatHeader conversation={conversation} onBack={onBack} onOpenInfo={onOpenInfo} />
			</Box>
			<Box flexShrink={0}>
				<OwnershipBar conversation={conversation} />
			</Box>

			<Box flex="1" position="relative" minH="0">
				<Wallpaper />
				{/* ★ THE THREAD SITS AT THE BOTTOM, THE WAY EVERY CHAT DOES. The inner
				    column is at least the full height and justified to its end, so a
				    short thread rests on the composer and a long one scrolls
				    normally. `minH` rather than `h` keeps the top reachable. */}
				<Box
					ref={scrollRef}
					h="100%"
					overflowY="auto"
					overflowX="hidden"
					position="relative"
					onScroll={onScroll}
					// Keeps a flick inside the thread instead of handing it to the
					// page once the top or bottom is reached.
					overscrollBehavior="contain"
				>
					<Flex direction="column" justify="flex-end" minH="100%" py="2">
						{/* The sentinel sits ABOVE the reserved slot, so it is reached a
						    little before the very top and the next page is already in
						    flight by the time the reader gets there. */}
						{hasMore && <Box ref={topRef} h="1px" />}
						{/* ★ CONSTANT HEIGHT WHETHER OR NOT IT IS LOADING — see
						    HistoryLoader. Mounting a spinner here on fetch and removing
						    it on arrival changed the content height twice around the
						    moment the scroll anchor was applied, which is a jump no
						    compensation can cancel. */}
						<HistoryLoader loading={loadingOlder} hasMore={hasMore} />
						{/* The FIRST page is a different event from an older page: there
						    is nothing on screen yet to preserve, so it is allowed a
						    plain centred spinner. */}
						{loadingMessages && messages.length === 0 && (
							<Flex justify="center" py="6">
								<Spinner size="sm" color="wa.inkMuted" />
							</Flex>
						)}
						{/* An empty thread has two very different causes. One we opened and
						    the customer has not answered — explain the template rule, or the
						    disabled composer below looks like a bug. Anything else is just
						    empty. */}
						{!loadingMessages && messages.length === 0 && !conversation.lastInboundAt && (
							<NewConversationBanner customerName={conversation.customerName} conversationId={conversation._id} />
						)}
						{!loadingMessages && messages.length === 0 && conversation.lastInboundAt && (
							<Flex justify="center" py="8">
								<Text fontSize="sm" color="wa.inkMuted" bg="wa.datePill" px="3" py="1" borderRadius="md">
									No messages yet
								</Text>
							</Flex>
						)}
						{items.map((item) => {
							if (item.kind === 'day') return <DayPill key={item.key} label={item.label} />;
							if (item.kind === 'unread') return <UnreadDivider key={item.key} count={item.count} />;
							if (item.kind === 'system') return <SystemNote key={item.key} text={item.text} />;
							const m = item.message;
							const quotedWamid =
								m.context?.wamid ?? (m.direction === 'outbound' && m.wamid ? p.quotes[m.wamid] ?? null : null);
							const quoted = quotedWamid ? byWamid.get(quotedWamid) ?? null : null;
							const isNew = known.primed && !known.keys.has(m.wamid) && !known.keys.has(m._id);
							const showSender = m.direction === 'outbound' && item.first && Boolean(m.sentBy);
							return (
								<MessageBubble
									key={item.key}
									message={m}
									conversationId={conversationId}
									first={item.first}
									isNew={isNew}
									quoted={quoted}
									quotedWamid={quotedWamid}
									quoteSender={quoteSenderOf(quoted)}
									senderLabel={showSender ? names(m.sentBy) : null}
									senderAvatar={showSender ? avatars(m.sentBy) : null}
									senderName={m.direction === 'outbound' ? names(m.sentBy) : ''}
									starred={Boolean(m.wamid && p.starred[m.wamid])}
									highlighted={highlightWamid === m.wamid}
									onReply={setReplyTo}
									onJumpTo={jumpTo}
									onOpenMedia={openMedia}
								/>
							);
						})}
						{typing && <TypingIndicator name={names(typing.userId)} />}
						<Box ref={bottomRef} h="1px" />
					</Flex>
				</Box>

				<JumpToLatest visible={!atBottom} count={pendingNew} onClick={() => scrollToBottom('smooth')} />
			</Box>

			<Box flexShrink={0}>
				<Composer
					conversation={conversation}
					replyTo={replyTo}
					replySender={quoteSenderOf(replyTo)}
					onCancelReply={() => setReplyTo(null)}
					numberState={numberState}
				/>
			</Box>

			{lightbox !== null && gallery[lightbox] && (
				<MediaLightbox
					isOpen
					onClose={() => setLightbox(null)}
					items={gallery}
					index={lightbox}
					onIndexChange={setLightbox}
					conversationId={conversationId}
				/>
			)}
		</Flex>
	);
}
